import { MASTER_DOCUMENT_TRANSLATIONS_ES } from '../i18n';
import type { TranslateFn } from '../i18nFactory';
import type { IMasterDocumentAdapter } from '../adapter/MasterDocumentAdapter.types';

/**
 * Fetcher semántico para las llamadas del panel de cruce (Modelo B — FEAT-094).
 *
 * Enruta SIEMPRE por `adapter.genericFetch` cuando está disponible, de modo que
 * la app consumidora controle un único punto de salida HTTP: inyección de
 * autenticación (`Authorization`, `X-Company-Id`), `baseUrl` real y, si lo
 * necesita, remapeo de las rutas canónicas del kit a las del backend
 * (p. ej. `/api/v1/crossing/*` → `/api/v1/crossings/*`).
 *
 * Solo cae a `fetch` directo contra `baseUrl` cuando el adapter NO implementa
 * `genericFetch` (escenarios de Storybook/demo sin backend autenticado). En ese
 * camino los errores HTTP se normalizan a un `Error` con `errorCode`/`data` para
 * que la clasificación FE-08 (inline vs dialog) siga funcionando.
 */
export type CrossingFetch = <T = unknown>(method: string, path: string, body?: unknown) => Promise<T>;

/**
 * Error normalizado de una operación de cruce. `errorCode` proviene del
 * `ProblemDetails` del backend (dependencia FE-08); `message` es texto de
 * usuario listo para mostrar (sin IDs internos ni stack traces).
 */
export interface CrossingError {
  errorCode?: string;
  message: string;
  /**
   * Clave i18n cuando el mensaje lo generó el kit y no el backend.
   *
   * `extractCrossingError` es una función pura consumida desde hooks, así que no
   * puede llamar a `useMasterDocumentTranslation`. Cuando el error no traía texto
   * propio del backend, propaga esta clave para que el hook llamante haga
   * `messageKey ? t(messageKey) : message`. Cuando el backend SÍ mandó texto queda
   * `undefined`: ese texto ya viene traducido (el kit envía `Accept-Language`) y
   * tiene prioridad.
   */
  messageKey?: string;
}

/**
 * Códigos de error de cruce que el PRD (FE-08) clasifica como de LÍNEA y que por
 * tanto deben mostrarse inline junto a la fila afectada (no en dialog/banner).
 * El resto (p. ej. BR-13/BR-15) se trata a nivel documento/página.
 */
const LINE_LEVEL_ERROR_CODES = new Set(['BR-01', 'BR-11', 'BR-16', 'BR01', 'BR11', 'BR16']);

export function isLineLevelCrossingError(errorCode: string | undefined): boolean {
  return errorCode ? LINE_LEVEL_ERROR_CODES.has(errorCode.toUpperCase()) : false;
}

/** Clave i18n del error genérico de cruce cuando el backend no manda texto propio. */
const FALLBACK_KEY = 'master_document.error.unexpected';
/** Texto español del fallback, tomado del catálogo para no duplicar el literal. */
const FALLBACK_MESSAGE = MASTER_DOCUMENT_TRANSLATIONS_ES[FALLBACK_KEY];

/**
 * Extrae `{ errorCode, message, messageKey }` de cualquier error lanzado por
 * `CrossingFetch`, cubriendo las formas más comunes: el propio `Error` con
 * `errorCode`/`data`, un `ProblemDetails` plano, o errores de cliente HTTP con
 * `response.data`.
 *
 * `messageKey` solo se rellena cuando el mensaje es el fallback del kit; si el
 * backend mandó texto, ese texto gana y `messageKey` queda `undefined`.
 */
export function extractCrossingError(err: unknown): CrossingError {
  if (typeof err === 'string') {
    return err ? { message: err } : { message: FALLBACK_MESSAGE, messageKey: FALLBACK_KEY };
  }
  if (err && typeof err === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const e = err as Record<string, any>;
    const data = e.response?.data ?? e.data ?? e.problem ?? e;
    const errorCode = data?.errorCode ?? data?.error_code ?? e.errorCode;
    const fromBackend = data?.detail ?? data?.message ?? data?.title ?? e.message;
    return {
      errorCode: errorCode != null ? String(errorCode) : undefined,
      message: fromBackend != null ? String(fromBackend) : FALLBACK_MESSAGE,
      messageKey: fromBackend != null ? undefined : FALLBACK_KEY,
    };
  }
  return { message: FALLBACK_MESSAGE, messageKey: FALLBACK_KEY };
}

/**
 * Igual que `extractCrossingError`, pero ya traducido al idioma activo.
 *
 * Recibe `t` en vez de llamar al hook porque este módulo es puro; los hooks de
 * cruce (`useCrossing`, `useCrossDocuments`, `useBulkConversion`) obtienen `t` de
 * `useMasterDocumentTranslation` y lo pasan aquí.
 */
export function resolveCrossingError(err: unknown, t: TranslateFn): CrossingError {
  const extracted = extractCrossingError(err);
  return extracted.messageKey
    ? { ...extracted, message: t(extracted.messageKey) }
    : extracted;
}

/**
 * Construye el `CrossingFetch` para una sesión de MasterDocument.
 * Prefiere `adapter.genericFetch`; si no existe, usa `fetch` directo + `baseUrl`.
 */
export function createCrossingFetch(
  adapter: IMasterDocumentAdapter,
  baseUrl: string,
): CrossingFetch {
  const generic = adapter.genericFetch?.bind(adapter);
  if (generic) {
    return <T = unknown,>(method: string, path: string, body?: unknown): Promise<T> =>
      generic(method, path, body) as Promise<T>;
  }

  return async <T = unknown,>(method: string, path: string, body?: unknown): Promise<T> => {
    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      throw await buildHttpError(res);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  };
}

async function buildHttpError(
  res: Response,
): Promise<Error & { errorCode?: string; data?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any;
  try {
    data = await res.json();
  } catch {
    /* respuesta sin cuerpo JSON */
  }
  const message = data?.detail ?? data?.message ?? data?.title ?? `HTTP ${res.status}`;
  const err = new Error(String(message)) as Error & { errorCode?: string; data?: unknown };
  err.errorCode = data?.errorCode ?? data?.error_code;
  err.data = data;
  return err;
}
